// Contract editor — opens from a building (or the list). Kind first; client ref (budget no.) + contractor's ref identify it;
// it covers one building, several, or all buildings in the project; money terms only when claims run against it.
// Saves through /api/write (audited); refuses a save made on an out-of-date copy.
import { useEffect, useMemo, useState } from 'react';
import { write } from '../../lib/api';
import { money } from '../../lib/claimCalc';
import { KINDS, type Building, type Contract, type ContractKind } from '../../lib/buildings';
import { FIJI_VAT, pctText, vatOn, type VatChange } from '../../lib/vat';
import { Button, Drawer, Field, errText, useToast } from '../kit';

type Org = { id: string; name: string; kind: string };
type Form = Record<string, string | boolean | string[] | { from: string; pct: string }[]>;

const NUM = ['sum_vep', 'deposit_pct', 'deposit_recovery_pct', 'retention_pct', 'retention_limit_pct', 'dlp_months', 'open_after_claim', 'open_gross_vep', 'open_variations_vep', 'open_deposit_recovered', 'open_retention_held', 'open_paid_vep'];
const TEXT = ['title', 'contract_no', 'project_no', 'employer_name', 'works_name', 'cert_code', 'signed_on', 'notes', 'contractor_org_id', 'architect_org_id', 'status'];
const STATUS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'practical_completion', label: 'Practical completion' },
  { value: 'closed', label: 'Closed' },
];

function toForm(c: Partial<Contract> | null, b: Building | null): Form {
  const f: Form = {};
  for (const k of [...NUM, ...TEXT]) {
    const v = (c as Record<string, unknown> | null)?.[k];
    f[k] = v === null || v === undefined ? '' : String(v);
  }
  f.kind = c?.kind ?? 'building';
  f.status = (c?.status as string) || 'active';
  f.has_claims = c ? Boolean(c.has_claims) : true;
  f.covers_all = Boolean(c?.covers_all);
  f.building_ids = c?.building_ids?.length ? [...c.building_ids] : b ? [b.id] : [];
  // VAT: blank = follow the Fiji rate by date; set = this contract's rate at signing + any changes during it
  f.vat_mode = c?.vat_pct !== null && c?.vat_pct !== undefined ? 'set' : 'auto';
  f.vat_pct = c?.vat_pct !== null && c?.vat_pct !== undefined ? String(c.vat_pct) : '';
  f.vat_changes = (c?.vat_changes ?? []).map((x) => ({ from: x.from, pct: String(x.pct) }));
  return f;
}

export function ContractEditor({
  open,
  contract,
  building,
  buildings,
  orgs,
  siteId,
  canEdit,
  onClose,
  onSaved,
}: {
  open: boolean;
  contract: Contract | null;
  building: Building | null;
  buildings: Building[];
  orgs: Org[];
  siteId: string;
  canEdit: boolean;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const toast = useToast();
  const [f, setF] = useState<Form>(() => toForm(contract, building));
  const [busy, setBusy] = useState(false);
  const [several, setSeveral] = useState(() => (contract?.building_ids?.length ?? 0) > 1);
  useEffect(() => {
    if (!open) return;
    setF(toForm(contract, building));
    setSeveral((contract?.building_ids?.length ?? 0) > 1);
  }, [open, contract, building]);

  const set = (k: string, v: string | boolean | string[]) => setF((x) => ({ ...x, [k]: v }));
  const kind = f.kind as ContractKind;
  const ids = f.building_ids as string[];
  const scope = f.covers_all ? 'all' : several || ids.length > 1 ? 'several' : 'one';
  const n = (k: string) => (String(f[k] ?? '').trim() === '' ? null : Number(String(f[k]).replace(/,/g, '')));

  const vatChanges = (f.vat_changes as { from: string; pct: string }[]) ?? [];
  const cleanChanges = (): VatChange[] =>
    vatChanges.filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x.from) && x.pct.trim() !== '' && Number.isFinite(Number(x.pct))).map((x) => ({ from: x.from, pct: Number(x.pct) }));
  const vatContract = f.vat_mode === 'set' ? { vat_pct: n('vat_pct'), vat_changes: cleanChanges() } : null;
  const vatNow = vatOn(vatContract);
  const vatSigned = vatOn(vatContract, String(f.signed_on || '') || null);
  const setChange = (i: number, k: 'from' | 'pct', v: string) => set('vat_changes', vatChanges.map((x, j) => (j === i ? { ...x, [k]: v } : x)) as never);

  const preview = useMemo(() => {
    const sum = n('sum_vep') ?? 0;
    const dep = (sum * (n('deposit_pct') ?? 0)) / 100;
    const works = 500000;
    const rec = Math.max(0, Math.min((dep * (n('deposit_recovery_pct') ?? 0)) / 100, dep - (n('open_deposit_recovered') ?? 0)));
    const claim = works - rec;
    const limit = (sum * (n('retention_limit_pct') ?? 0)) / 100;
    const ret = limit > 0 ? Math.max(0, Math.min((claim * (n('retention_pct') ?? 0)) / 100, limit - (n('open_retention_held') ?? 0))) : (claim * (n('retention_pct') ?? 0)) / 100;
    const vep = claim - ret;
    return { works, dep, rec, claim, limit, ret, vep, vat: (vep * vatNow) / 100, vip: vep * (1 + vatNow / 100) };
  }, [f, vatNow]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    if (!String(f.title).trim()) return toast({ tone: 'error', text: 'Give the contract a title' });
    if (!f.covers_all && !ids.length) return toast({ tone: 'error', text: 'Pick at least one building, or "All buildings in the project"' });
    for (const k of NUM) {
      const v = n(k);
      if (v !== null && !Number.isFinite(v)) return toast({ tone: 'error', text: `${k.replace(/_/g, ' ')} is not a number` });
    }
    const values: Record<string, unknown> = { kind, has_claims: Boolean(f.has_claims), covers_all: Boolean(f.covers_all), building_ids: ids };
    for (const k of TEXT) values[k] = String(f[k] ?? '').trim() || null;
    for (const k of NUM) values[k] = n(k);
    values.status = values.status ?? 'active';
    if (f.vat_mode === 'set') {
      const v = n('vat_pct');
      if (v === null || !Number.isFinite(v) || v < 0 || v > 50) return toast({ tone: 'error', text: 'Type the VAT rate at signing (e.g. 15)' });
      values.vat_pct = v;
      values.vat_changes = cleanChanges();
    } else {
      values.vat_pct = null;
      values.vat_changes = [];
    }
    setBusy(true);
    try {
      if (contract) {
        await write('contracts', 'update', { id: contract.id, values: { ...values, _expect_updated_at: contract.updated_at }, summary: `Edited contract ${values.title}` });
        onSaved(contract.id);
      } else {
        const row = await write<{ id: string }>('contracts', 'insert', { values: { ...values, site_id: siteId }, summary: `Added contract ${values.title}` });
        onSaved(row.id);
      }
      toast({ tone: 'ok', text: 'Contract saved' });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  const sel = (k: string, list: Org[]) => (
    <select value={String(f[k] ?? '')} onChange={(e) => set(k, e.target.value)} disabled={!canEdit}>
      <option value="">—</option>
      {list.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );
  const inp = (k: string, ph = '', type = 'text') => (
    <input type={type} inputMode={NUM.includes(k) ? 'decimal' : undefined} value={String(f[k] ?? '')} placeholder={ph} onChange={(e) => set(k, e.target.value)} disabled={!canEdit} />
  );
  const party = kind === 'consultant' ? 'Consultant' : kind === 'subcontract' ? 'Subcontractor' : 'Contractor';
  const partyOrgs = orgs.filter((o) => (kind === 'consultant' ? ['architect', 'engineer', 'consultant', 'other'] : ['contractor', 'supplier', 'other']).includes(o.kind));

  return (
    <Drawer
      open={open}
      title={contract ? `Contract — ${contract.title ?? contract.contract_no ?? ''}` : building ? `New contract — ${building.name}` : 'New contract'}
      onClose={onClose}
      footer={
        canEdit ? (
          <div className="resp-row">
            <span className="muted small grow">Every change is logged. Once a claim is certified, changes apply to later claims.</span>
            <Button onClick={onClose}>Cancel</Button>
            <Button kind="primary" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save contract'}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="ce">
        <div className="ce-sec"><i>1</i> Contract &amp; parties</div>
        <Field label="Kind of contract" group>
          <div className="bseg" role="radiogroup" aria-label="Kind of contract">
            {KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                role="radio"
                aria-checked={kind === k.value}
                className={kind === k.value ? 'on' : ''}
                disabled={!canEdit}
                onClick={() => {
                  set('kind', k.value);
                  if (!contract) set('has_claims', k.value === 'building');
                  if (k.value === 'main' && !contract) set('covers_all', true);
                }}
              >
                {k.label}
              </button>
            ))}
          </div>
        </Field>
        <div className="ce-grid">
          <Field label="Client ref (budget no.)" hint={kind === 'main' ? 'Often none — a master agreement uses each building\'s budget no.' : 'With the contractor\'s ref, the unique ID'}>
            {inp('contract_no', kind === 'main' ? 'none' : 'e.g. 77015658')}
          </Field>
          <Field label="Contractor's ref" hint="Their own project / quote no.">
            {inp('project_no', 'e.g. 1425')}
          </Field>
          <Field label="Title">{inp('title', 'e.g. Treehouse building works')}</Field>
          <Field label={party}>{sel('contractor_org_id', partyOrgs)}</Field>
          <Field label="Architect of record" hint="Signs the payment certificates">
            {sel('architect_org_id', orgs.filter((o) => o.kind === 'architect'))}
          </Field>
          <Field label="Employer (on the certificate)">{inp('employer_name', 'e.g. Aquarius Tours Pte Ltd')}</Field>
          <Field label="Signed on">{inp('signed_on', '', 'date')}</Field>
          <Field label="Status">
            <select value={String(f.status)} onChange={(e) => set('status', e.target.value)} disabled={!canEdit}>
              {STATUS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Covers" group hint={scope === 'all' ? 'Shown on every building in the project, so each close-out download includes it.' : 'Shown on each ticked building, so each close-out download includes it.'}>
          <div className="bseg" role="radiogroup" aria-label="What this contract covers">
            {[
              ['one', 'This building'],
              ['several', 'Several buildings'],
              ['all', 'All buildings in the project'],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={scope === k}
                className={scope === k ? 'on' : ''}
                disabled={!canEdit}
                onClick={() => {
                  setSeveral(k === 'several');
                  if (k === 'all') set('covers_all', true);
                  else {
                    set('covers_all', false);
                    if (k === 'one') set('building_ids', building ? [building.id] : ids.slice(0, 1));
                    if (k === 'several' && ids.length < 2) set('building_ids', ids.length ? ids : building ? [building.id] : []);
                  }
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {scope !== 'all' ? (
            <div className="bchips">
              {buildings
                .filter((b) => !b.deleted_at)
                .map((b) => {
                  const on = ids.includes(b.id);
                  if (scope === 'one' && !on && building) return null;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      className={`chip ${on ? 'on' : ''}`}
                      aria-pressed={on}
                      disabled={!canEdit}
                      onClick={() => set('building_ids', on ? ids.filter((x) => x !== b.id) : scope === 'one' ? [b.id] : [...ids, b.id])}
                    >
                      {on ? '✓ ' : ''}
                      {b.short_code ? `${b.short_code} ` : ''}
                      {b.name}
                    </button>
                  );
                })}
            </div>
          ) : null}
        </Field>
        <label className="ce-claims">
          <input type="checkbox" checked={Boolean(f.has_claims)} onChange={(e) => set('has_claims', e.target.checked)} disabled={!canEdit} />
          <span>
            <b>Claims are made against this contract</b>
            <span className="muted small">Turns on the money terms, certificate and opening totals. Leave off for agreements kept for the record.</span>
          </span>
        </label>

        {f.has_claims ? (
          <>
            <div className="ce-sec"><i>2</i> Money terms</div>
            <div className="ce-grid three">
              <Field label="Contract sum · VEP" hint={n('sum_vep') ? `VIP ${money((n('sum_vep') ?? 0) * (1 + vatSigned / 100))} at ${pctText(vatSigned)}${vatNow !== vatSigned ? ` · ${money((n('sum_vep') ?? 0) * (1 + vatNow / 100))} at ${pctText(vatNow)} now` : ''}` : undefined}>
                {inp('sum_vep', '0.00')}
              </Field>
              <Field label="Deposit · % of contract" hint={n('sum_vep') && n('deposit_pct') ? `${money(preview.dep)} VEP` : undefined}>
                {inp('deposit_pct', 'e.g. 10')}
              </Field>
              <Field label="Recovered per claim · % of deposit" hint="until the deposit is all back">
                {inp('deposit_recovery_pct', 'e.g. 10')}
              </Field>
              <Field label="Retention · % per claim">{inp('retention_pct', 'e.g. 5')}</Field>
              <Field label="Retention limit · %" hint="of contract + approved variations">
                {inp('retention_limit_pct', 'e.g. 5')}
              </Field>
              <Field label="Defects liability · months">{inp('dlp_months', 'e.g. 12')}</Field>
            </div>
            <div className="ce-vat" role="group" aria-label="VAT rate">
              <span className="field-label">VAT</span>
              <label className="check">
                <input type="radio" name="vat_mode" checked={f.vat_mode !== 'set'} onChange={() => set('vat_mode', 'auto')} disabled={!canEdit} />
                <span>
                  Fiji rate by date <span className="muted small">({FIJI_VAT.slice(1).map((x) => `${pctText(x.pct)} from ${x.from.split('-').reverse().join('/')}`).join(' → ')})</span>
                </span>
              </label>
              <label className="check">
                <input type="radio" name="vat_mode" checked={f.vat_mode === 'set'} onChange={() => set('vat_mode', 'set')} disabled={!canEdit} />
                <span>Set the rate for this contract</span>
              </label>
              {f.vat_mode === 'set' ? (
                <div className="ce-vat-set">
                  <Field label="VAT at signing · %">{inp('vat_pct', 'e.g. 15')}</Field>
                  {vatChanges.map((x, i) => (
                    <div key={i} className="ce-vat-row">
                      <Field label="Changed to · %">
                        <input inputMode="decimal" value={x.pct} onChange={(e) => setChange(i, 'pct', e.target.value)} disabled={!canEdit} placeholder="e.g. 12.5" />
                      </Field>
                      <Field label="From">
                        <input type="date" value={x.from} onChange={(e) => setChange(i, 'from', e.target.value)} disabled={!canEdit} />
                      </Field>
                      {canEdit ? (
                        <Button kind="ghost" onClick={() => set('vat_changes', vatChanges.filter((_, j) => j !== i) as never)} aria-label="Remove this VAT change">
                          Remove
                        </Button>
                      ) : null}
                    </div>
                  ))}
                  {canEdit ? (
                    <div className="ce-vat-add">
                      <Button kind="ghost" onClick={() => set('vat_changes', [...vatChanges, { from: '', pct: '' }] as never)}>
                        + VAT changed during the contract
                      </Button>
                      {!vatChanges.some((x) => x.from === '2025-08-01') ? (
                        <Button kind="ghost" onClick={() => set('vat_changes', [...vatChanges, { from: '2025-08-01', pct: '12.5' }] as never)}>
                          + 12.5 % from 1 Aug 2025
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
              <span className="muted small">
                Now {pctText(vatNow)}
                {f.signed_on ? ` · at signing ${pctText(vatSigned)}` : ''}. Claims use the rate on their period date.
              </span>
            </div>
            <div className="ce-sec"><i>3</i> Payment certificate</div>
            <div className="ce-grid three">
              <Field label="Works name on certificate">{inp('works_name', 'e.g. Tavarua Phase 2 — Treehouse')}</Field>
              <Field label="Certificate code" hint="SWI-TAV-PC-<code>-<claim no>">
                {inp('cert_code', 'e.g. TH')}
              </Field>
            </div>
            <div className="ce-sec"><i>4</i> Opening totals — claims before PM C3</div>
            <div className="ce-grid three">
              <Field label="After claim no.">{inp('open_after_claim', 'e.g. 12')}</Field>
              <Field label="Works certified to date · VEP">{inp('open_gross_vep')}</Field>
              <Field label="Variations to date · VEP">{inp('open_variations_vep')}</Field>
              <Field label="Deposit recovered to date">{inp('open_deposit_recovered')}</Field>
              <Field label="Retention held to date">{inp('open_retention_held')}</Field>
              <Field label="Paid to date · VEP">{inp('open_paid_vep')}</Field>
            </div>
            <p className="muted small" style={{ margin: 0 }}>
              From the last certificate before PM C3. Once a claim is certified in PM C3, running totals come from the claims.
            </p>
            {n('sum_vep') ? (
              <div className="ce-preview" aria-label="Preview of the next claim">
                <div className="k">Preview — a claim of {money(preview.works)} VEP works</div>
                <table className="t">
                  <tbody>
                    <tr>
                      <td>Less deposit recovered</td>
                      <td className="right">− {money(preview.rec)}</td>
                    </tr>
                    <tr className="sub2">
                      <td>Total claim (VEP)</td>
                      <td className="right">{money(preview.claim)}</td>
                    </tr>
                    <tr>
                      <td>Less retention{preview.limit ? ` (limit ${money(preview.limit)})` : ''}</td>
                      <td className="right">− {money(preview.ret)}</td>
                    </tr>
                    <tr>
                      <td>Add VAT {pctText(vatNow)}</td>
                      <td className="right">{money(preview.vat)}</td>
                    </tr>
                    <tr className="tot">
                      <td>Total payable (VIP)</td>
                      <td className="right">{money(preview.vip)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
        <Field label="Notes">
          <textarea rows={3} value={String(f.notes ?? '')} onChange={(e) => set('notes', e.target.value)} disabled={!canEdit} />
        </Field>
      </div>
    </Drawer>
  );
}
